import { formatLocale, type AnyLanguage } from './locales.ts';

/**
 * Formats always go through Intl, never by hand. Instances are cached: building an Intl formatter is costly.
 * Dates are stored in UTC and displayed in the organization's time zone.
 */
export function createFormatters(language: AnyLanguage, timeZone = 'Europe/Paris') {
    const locale = formatLocale[language];
    const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>();
    const cached = <T extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>(
        key: string,
        create: () => T,
    ): T => {
        let formatter = cache.get(key) as T | undefined;
        if (!formatter) {
            formatter = create();
            cache.set(key, formatter);
        }
        return formatter;
    };

    return {
        locale,
        date: (value: Date) =>
            cached('date', () => new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone })).format(value),
        /** Compact date for dense layouts: "13 nov. 2026". */
        shortDate: (value: Date) =>
            cached('shortDate', () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone })).format(value),
        /** A civil YYYY-MM-DD has no time zone; never shift it into the recipient's previous day. */
        calendarDate: (value: string) =>
            cached(
                'calendarDate',
                () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }),
            ).format(new Date(value)),
        calendarMonthRange: (start: string, end: string) =>
            cached(
                'calendarMonthRange',
                () => new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }),
            ).formatRange(new Date(start), new Date(end)),
        time: (value: Date) =>
            cached('time', () => new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone })).format(value),
        /** Month span: "juil. – août 2027", or "juil. 2027" when both dates fall in the same month. */
        monthRange: (start: Date, end: Date) =>
            cached(
                'monthRange',
                () => new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone }),
            ).formatRange(start, end),
        dateTime: (value: Date) =>
            cached(
                'dateTime',
                () => new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short', timeZone }),
            ).format(value),
        number: (value: number, maximumFractionDigits = 2) =>
            cached(
                `number:${maximumFractionDigits}`,
                () => new Intl.NumberFormat(locale, { maximumFractionDigits }),
            ).format(value),
        currency: (value: number, currency = 'EUR') =>
            cached(`currency:${currency}`, () => new Intl.NumberFormat(locale, { style: 'currency', currency })).format(
                value,
            ),
        /** Whole days from `reference` to `value`: "dans 24 jours", "il y a 2 jours". */
        relativeDays: (value: Date, reference: Date) =>
            cached('relative', () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })).format(
                Math.round((value.getTime() - reference.getTime()) / 86_400_000),
                'day',
            ),
    };
}

export type Formatters = ReturnType<typeof createFormatters>;
